import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { AnthropicService } from '../../services/api/anthropic/anthropic.service';
import { AITokenUsageService } from '../../services/api/ai-token-usage/ai-token-usage.service';
import { AIAgentService } from '../../services/api/agent/ai-agent.service';
import { SanitizePipe } from '../../pipes/sanitize.pipe';
import {
  AttachedImage,
  ConversationMessage,
  PageGenerationResult
} from '../../models/anthropic/anthropic.model';
import { AgentRequest, AgentResponse, CreatedEntitySummary, PendingAgentConfirmation } from '../../models/agent/agent.model';

/**
 * Integrated AI Assistant for Preference Page Management
 * Uses Anthropic Claude to provide conversational assistance with:
 * - Natural language commands for page manipulation
 * - Integrated HTML/CSS/JS generation
 * - Image-based style references
 * - Context-aware suggestions
 */
@Component({
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SanitizePipe,
    TranslateModule
  ],
  selector: 'app-anthropic-ai-assistant',
  standalone: true,
  styleUrls: ['./anthropic-ai-assistant.component.scss'],
  templateUrl: './anthropic-ai-assistant.component.html'
})
export class AnthropicAIAssistantComponent implements OnInit, AfterViewChecked, OnDestroy {
  /** When true, routes messages through the AI Agent write infrastructure instead of the page builder flow */
  @Input() public agentMode: boolean = false;

  /** Available core contacts */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public availableCoreContacts: any[] = [];

  /** Available core preferences */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public availableCorePreferences: any[] = [];

  /** Current CSS */
  @Input() public currentCss: string = '';

  /** Current Footer HTML */
  @Input() public currentFooterHtml: string = '';

  /** Current HTML */
  @Input() public currentHtml: string = '';

  /** Current JavaScript */
  @Input() public currentScript: string = '';

  /** Current page controls (for context) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public currentControls: any[] = [];

  /** Whether to show the assistant */
  @Input() public isOpen: boolean = false;

  /** Current preference page ID */
  @Input() public preferencePageId: string = '';

  /** Tenant ID for conversation management */
  @Input() public tenantId: string = '';

  /** User ID (Auth0 sub) for conversation persistence */
  @Input() public userId: string = '';

  /** Emits when CSS should be updated */
  @Output() public cssUpdated: EventEmitter<string> = new EventEmitter<string>();

  /** Emits when controls should be updated */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() public controlsUpdated: EventEmitter<any[]> = new EventEmitter<any[]>();

  /** Emits when HTML should be updated */
  @Output() public htmlUpdated: EventEmitter<string> = new EventEmitter<string>();

  /** Emits when assistant is closed */
  @Output() public modalClosed: EventEmitter<void> = new EventEmitter<void>();

  /** Emits when a full page update is needed */
  @Output() public pageUpdated: EventEmitter<PageGenerationResult> = new EventEmitter<PageGenerationResult>();

  /** Emits when JavaScript should be updated */
  @Output() public scriptUpdated: EventEmitter<string> = new EventEmitter<string>();

  // Agent confirmation state
  public agentConversationId: string | undefined = undefined;

  // Image upload
  @ViewChild('fileInput') public fileInput!: ElementRef<HTMLInputElement>;

  public attachedImages: AttachedImage[] = [];

  // Conversation state
  public conversationId: string | undefined = undefined;

  public error: string = '';

  public isProcessing: boolean = false;

  public maxImages: number = 5;

  public messages: ConversationMessage[] = [];

  public pendingAgentConfirmation: PendingAgentConfirmation | undefined = undefined;

  // Agent result card — set when a PreferencePage entity is created
  public lastCreatedChildEntities: CreatedEntitySummary[] = [];

  public lastCreatedPageEntity: CreatedEntitySummary | undefined = undefined;

  // Preview and apply
  public pendingChanges: PageGenerationResult | undefined = undefined;

  @ViewChild('previewIframe') public previewIframe!: ElementRef<HTMLIFrameElement>;

  public previewHtml: string = '';

  public previewTab: 'preview' | 'code' = 'preview';

  // Quick actions
  public quickActions: Array<{ icon: string; label: string; prompt: string }> = [
    { icon: 'add', label: 'Add Control', prompt: 'Add a new control to the page' },
    { icon: 'delete', label: 'Remove Control', prompt: 'Remove a control from the page' },
    { icon: 'swap_vert', label: 'Reorder', prompt: 'Reorder the controls on the page' },
    { icon: 'palette', label: 'Style Update', prompt: 'Update the visual style of the page' },
    { icon: 'code', label: 'Add Functionality', prompt: 'Add JavaScript functionality' },
    { icon: 'devices', label: 'Make Responsive', prompt: 'Make the page responsive for all devices' }
  ];

  // Token usage
  public sessionTokens: number = 0;

  public showPreview: boolean = false;

  public userMessage: string = '';

  public userTotalTokens: number = 0;

  private readonly destroy$: Subject<void> = new Subject<void>();
  private previewHtmlApplied: string = '';

  public constructor(
    private anthropicService: AnthropicService,
    private tokenUsageService: AITokenUsageService,
    private aiAgentService: AIAgentService,
    private router: Router
  ) {}

  public ngOnInit(): void {
    if (this.isOpen && !this.conversationId) {
      this.initializeConversation();
    }

    // Token usage subscriptions
    this.tokenUsageService.sessionTokens$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((tokens: number): void => {
      this.sessionTokens = tokens;
    });

    this.tokenUsageService.userTotal$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((summary: { totalTokens: number } | undefined): void => {
      this.userTotalTokens = summary?.totalTokens ?? 0;
    });

    this.tokenUsageService.getUserSummary();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngAfterViewChecked(): void {
    // Write srcdoc directly to the DOM to bypass Angular sanitization
    if (this.previewIframe && this.previewHtml && this.previewHtml !== this.previewHtmlApplied) {
      this.previewIframe.nativeElement.srcdoc = this.previewHtml;
      this.previewHtmlApplied = this.previewHtml;
    }
  }

  /**
   * Initialize or restore conversation.
   * First tries to find an existing conversation for this user + tenant + page.
   * If found, restores the messages. Otherwise creates a new one.
   */
  public async initializeConversation(): Promise<void> {
    try {
      // Try to restore existing conversation for this user + tenant + page
      if (this.userId && this.tenantId && this.preferencePageId) {
        const existing: { conversationId: string; messages: ConversationMessage[] } | undefined =
          await this.anthropicService.getConversationByUserAndPage(
            this.userId,
            this.tenantId,
            this.preferencePageId
          ).toPromise();

        if (existing) {
          this.conversationId = existing.conversationId;
          this.messages = this.parseRestoredMessages(existing.messages);

          if (this.messages.length === 0) {
            this.addWelcomeMessage();
          }
          return;
        }
      }

      // No existing conversation found — create a new one
      const context: { conversationId: string; messages: ConversationMessage[] } | undefined =
        await this.anthropicService.createConversation(
          this.tenantId,
          'preference-page-builder',
          this.userId,
          this.preferencePageId
        ).toPromise();

      if (context) {
        this.conversationId = context.conversationId;
        this.messages = context.messages || [];
        this.addWelcomeMessage();
      }
    } catch {
      this.error = 'Failed to initialize AI assistant. Please try again.';
    }
  }

  /**
   * Parse messages restored from MongoDB.
   * Backend stores Content as List<MessageContent> (array of objects),
   * but the frontend chat uses content as a plain string.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseRestoredMessages(messages: any[]): ConversationMessage[] {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return messages.map((msg: any): ConversationMessage => {
      let content: string = '';

      // Content from MongoDB is List<MessageContent> = [{type:"text", text:"..."}, ...]
      if (Array.isArray(msg.content)) {
        content = msg.content
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((c: any): boolean => c.type === 'text' && c.text)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((c: any): string => c.text)
          .join('\n');
      } else {
        content = this.extractStringFromMessage(msg.content);
      }

      const result: ConversationMessage = {
        content,
        role: msg.role,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
      };
      // For assistant messages with code blocks, set displayContent to stripped version
      if (msg.role === 'assistant' && content && /```[\w]*\n/.test(content)) {
        result.displayContent = this.stripCodeBlocks(content);
      }
      return result;
    }).filter((msg: ConversationMessage): boolean => !!msg.content); // Skip empty messages
  }

  private addWelcomeMessage(): void {
    if (this.messages.length === 0) {
      this.addSystemMessage(
        'Hello! I\'m your Preference Page Assistant. I can help you:\n\n' +
        '• Add, remove, or reorder controls\n' +
        '• Generate complete HTML/CSS/JavaScript\n' +
        '• Apply styles from reference images\n' +
        '• Make your page responsive and accessible\n\n' +
        'What would you like to do?'
      );
    }
  }

  /**
   * Send message to AI
   */
  public async sendMessage(): Promise<void> {
    if (!this.userMessage.trim() && this.attachedImages.length === 0) {
      return;
    }

    // Route to agent write infrastructure when in agent mode
    if (this.agentMode) {
      this.sendAgentMessage(this.userMessage.trim());
      this.userMessage = '';
      return;
    }

    if (!this.conversationId) {
      await this.initializeConversation();
    }

    const userMsg: string = this.userMessage.trim();
    this.addUserMessage(userMsg);

    this.isProcessing = true;
    this.error = '';
    this.userMessage = '';

    try {
      const context: Record<string, string> = this.buildContext();

      // Build request with optional conversationId and userId
      const request: {
        conversationId?: string;
        context?: Record<string, string>;
        images?: AttachedImage[];
        tenantId: string;
        userId: string;
        userMessage: string;
      } = {
        tenantId: this.tenantId,
        userId: this.userId,
        userMessage: userMsg
      };

      // Only add context if it has properties
      if (Object.keys(context).length > 0) {
        request.context = context;
      }

      // Only include conversationId if it exists
      if (this.conversationId) {
        request.conversationId = this.conversationId;
      }

      // Only include images if there are any
      if (this.attachedImages && this.attachedImages.length > 0) {
        request.images = this.attachedImages;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await this.anthropicService.continueConversation(request).toPromise();

      if (response?.success) {
        // Handle both property names: assistantMessage (C#) and response (legacy)
        let message: string = response.assistantMessage || response.response || '';

        // Fix for [object Object] issue: Ensure message is a string
        message = this.extractStringFromMessage(message);

        // Update conversationId from response if provided
        if (response.conversationId && !this.conversationId) {
          this.conversationId = response.conversationId;
        }

        // Parse generated code from response
        const generatedCode: PageGenerationResult | undefined = this.parseGeneratedCode(response);

        // Validate and fix conversation history (do this BEFORE adding our message
        // so it doesn't overwrite displayContent)
        if (response.conversationHistory && Array.isArray(response.conversationHistory)) {
          this.messages = this.parseRestoredMessages(response.conversationHistory);
        } else {
          // No history from backend, add message locally
          this.addAssistantMessage(message);
        }

        // If there's generated code, ensure the last assistant message
        // has a clean displayContent (no code blocks) for the UI
        if (generatedCode) {
          const lastAssistant: ConversationMessage | undefined = this.findLastAssistantMessage();
          if (lastAssistant) {
            lastAssistant.displayContent = this.stripCodeBlocks(lastAssistant.content);
          }
          this.pendingChanges = generatedCode;
          this.previewTab = 'preview';
          this.buildPreviewHtml();
          this.showPreview = true;
        }

        this.clearAttachedImages();

        // Track token usage from response
        const usage: { totalTokens?: number; TotalTokens?: number } | undefined =
          response.usage || response.Usage;
        if (usage) {
          const totalTokens: number = usage.totalTokens || usage.TotalTokens || 0;
          if (totalTokens > 0) {
            this.tokenUsageService.addSessionTokens(totalTokens);
            this.tokenUsageService.getUserSummary();
          }
        }
      } else {
        this.error = response?.errorMessage || 'Failed to process message';
      }
    } catch (err: unknown) {
      const errorObj: { error?: { message?: string }; message?: string } =
        err as { error?: { message?: string }; message?: string };
      let errorMessage: string = 'An error occurred. Please try again.';
      if (errorObj?.error?.message) {
        errorMessage = errorObj.error.message;
      } else if (errorObj?.message) {
        errorMessage = errorObj.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      this.error = errorMessage;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Parse generated code from the response
   * Handles both camelCase (TypeScript) and PascalCase (C#) property names
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseGeneratedCode(response: Record<string, any>): PageGenerationResult | undefined {
    try {
      // Check for GeneratedCode (C# PascalCase) or generatedCode (TypeScript camelCase)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/dot-notation
      const generatedCode: Record<string, any> = response.generatedCode || response['GeneratedCode'];

      // Check for both sections and Sections (C# uses PascalCase)
      // eslint-disable-next-line @typescript-eslint/dot-notation
      if (generatedCode && (generatedCode.sections || generatedCode['Sections'])) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/dot-notation
        const sections: Record<string, any> = generatedCode.sections || generatedCode['Sections'];
        const sectionKeys: string[] = Object.keys(sections);

        const result: PageGenerationResult = {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          explanation: generatedCode.explanation || generatedCode['Explanation'] ||
                       // eslint-disable-next-line @typescript-eslint/dot-notation
                       response.assistantMessage || response['AssistantMessage'] ||
                       response.response || ''
        };

        // Iterate through keys and map them case-insensitively
        for (const key of sectionKeys) {
          const lowerKey: string = key.toLowerCase();
          const value: string = sections[key];

          switch (lowerKey) {
            case 'headerhtml': {
              result.headerHtml = value;
              break;
            }
            case 'footerhtml': {
              result.footerHtml = value;
              break;
            }
            case 'css': {
              result.css = value;
              break;
            }
            case 'javascript': {
              result.javascript = value;
              break;
            }
            default:
              break;
          }
        }

        // Return only if we have at least one section
        if (result.headerHtml || result.footerHtml || result.css || result.javascript) {
          return result;
        }
      }

      // Fallback: check if metadata contains suggestedChanges
      if (response.metadata?.suggestedChanges) {
        return response.metadata.suggestedChanges as PageGenerationResult;
      }

      // Fallback: check if Metadata contains suggestedChanges (C# PascalCase)
      // eslint-disable-next-line @typescript-eslint/dot-notation
      if (response['Metadata']?.suggestedChanges) {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        return response['Metadata'].suggestedChanges as PageGenerationResult;
      }

      return undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Build context object for AI
   * Serializes complex objects to JSON strings for backend compatibility
   */
  private buildContext(): Record<string, string> {
    const context: Record<string, string> = {};

    // Add only non-empty values
    if (this.preferencePageId) {
      context.preferencePageId = this.preferencePageId;
    }

    if (this.currentHtml) {
      context.currentHtml = this.currentHtml;
    }

    if (this.currentCss) {
      context.currentCss = this.currentCss;
    }

    if (this.currentScript) {
      context.currentScript = this.currentScript;
    }

    // Safely serialize currentControls
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controls: any[] = Array.isArray(this.currentControls) ? this.currentControls : [];
      if (controls.length > 0) {
        context.currentControls = JSON.stringify(controls);
      }
    } catch {
      // Ignore serialization errors
    }

    // Safely serialize availableCorePreferences
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const prefs: any[] = Array.isArray(this.availableCorePreferences)
        ? this.availableCorePreferences
        : [];
      if (prefs.length > 0) {
        context.availableCorePreferences = JSON.stringify(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          prefs.map((p: any): { description: string; id: string; name: string; type: string } => ({
            description: p?.description || '',
            id: p?.id || '',
            name: p?.name || '',
            type: p?.type || ''
          }))
        );
      }
    } catch {
      // Ignore serialization errors
    }

    // Safely serialize availableCoreContacts
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const contacts: any[] = Array.isArray(this.availableCoreContacts)
        ? this.availableCoreContacts
        : [];
      if (contacts.length > 0) {
        context.availableCoreContacts = JSON.stringify(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          contacts.map((c: any): { description: string; id: string; name: string; type: string } => ({
            description: c?.description || '',
            id: c?.id || '',
            name: c?.name || '',
            type: c?.type || ''
          }))
        );
      }
    } catch {
      // Ignore serialization errors
    }

    return context;
  }

  /**
   * Extract string from message that might be an object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractStringFromMessage(message: any): string {
    if (!message) {
      return '';
    }

    // If already a string, return it
    if (typeof message === 'string') {
      return message;
    }

    // If it's an object, try to extract the text content
    if (typeof message === 'object') {
      // Try common property names
      if (message.content && typeof message.content === 'string') {
        return message.content;
      }
      if (message.text && typeof message.text === 'string') {
        return message.text;
      }
      if (message.message && typeof message.message === 'string') {
        return message.message;
      }
      if (message.value && typeof message.value === 'string') {
        return message.value;
      }

      // If it's an array, try to extract text from first element
      if (Array.isArray(message) && message.length > 0) {
        return this.extractStringFromMessage(message[0]);
      }

      // Last resort: stringify the object
      // eslint-disable-next-line no-null/no-null
      return JSON.stringify(message, null, 2);
    }

    // Convert to string
    return String(message);
  }

  /**
   * Add quick action to prompt
   */
  public addQuickAction(prompt: string): void {
    this.userMessage = prompt;
  }

  /**
   * Handle image upload
   */
  public onFileSelected(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }

    const files: File[] = Array.from(input.files);

    files.forEach((file: File): void => {
      if (this.attachedImages.length >= this.maxImages) {
        this.error = `Maximum ${this.maxImages} images allowed`;
        return;
      }

      if (!file.type.startsWith('image/')) {
        this.error = 'Only image files are allowed';
        return;
      }

      const reader: FileReader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        const base64: string = e.target?.result as string;
        const base64Data: string = base64.split(',')[1]; // Remove data:image/xxx;base64, prefix

        this.attachedImages.push({
          base64Data,
          fileName: file.name,
          mediaType: file.type
        });
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    input.value = '';
  }

  /**
   * Remove attached image
   */
  public removeImage(index: number): void {
    this.attachedImages.splice(index, 1);
  }

  /**
   * Clear all attached images
   */
  public clearAttachedImages(): void {
    this.attachedImages = [];
  }

  /**
   * Trigger file input click
   */
  public triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Apply pending changes
   */
  public applyChanges(): void {
    if (!this.pendingChanges) {
      return;
    }

    const changes: PageGenerationResult = this.pendingChanges;

    if (changes.controls) {
      this.controlsUpdated.emit(changes.controls);
    }

    if (changes.headerHtml) {
      this.htmlUpdated.emit(changes.headerHtml);
    }

    if (changes.css) {
      this.cssUpdated.emit(changes.css);
    }

    if (changes.javascript) {
      this.scriptUpdated.emit(changes.javascript);
    }

    // Emit complete page update with all changes
    this.pageUpdated.emit(changes);

    const appliedSections: string[] = [];
    if (changes.headerHtml) {
      appliedSections.push('Header HTML');
    }
    if (changes.footerHtml) {
      appliedSections.push('Footer HTML');
    }
    if (changes.css) {
      appliedSections.push('CSS');
    }
    if (changes.javascript) {
      appliedSections.push('JavaScript');
    }
    if (changes.controls) {
      appliedSections.push('Controls');
    }

    const message: string = appliedSections.length > 0
      ? `Applied changes: ${appliedSections.join(', ')}`
      : 'Changes applied successfully!';

    this.addSystemMessage(message);
    this.pendingChanges = undefined;
    this.showPreview = false;
    this.previewTab = 'preview';
    this.previewHtml = '';
    this.previewHtmlApplied = '';
  }

  /**
   * Reject pending changes
   */
  public rejectChanges(): void {
    this.pendingChanges = undefined;
    this.showPreview = false;
    this.previewTab = 'preview';
    this.previewHtml = '';
    this.previewHtmlApplied = '';
    this.addSystemMessage('Changes discarded. Feel free to try a different approach!');
  }

  /**
   * Add user message to chat
   */
  private addUserMessage(content: string): void {
    const messageContent: string = this.extractStringFromMessage(content);
    this.messages.push({
      content: messageContent,
      role: 'user',
      timestamp: new Date()
    });
  }

  /**
   * Add assistant message to chat
   *
   * @param content Full content (kept for AI context)
   * @param displayContent Optional cleaned version for UI display
   */
  private addAssistantMessage(content: string, displayContent?: string): void {
    const messageContent: string = this.extractStringFromMessage(content);
    const msg: ConversationMessage = {
      content: messageContent,
      displayContent: this.markdownToHtml(displayContent || messageContent),
      role: 'assistant',
      timestamp: new Date()
    };
    this.messages.push(msg);
  }

  /**
   * Convert markdown to HTML for display.
   * Handles: **bold**, `code`, tables (| col |), bullet lists (- item), line breaks.
   */
  private markdownToHtml(text: string): string {
    if (!text) {
      return text;
    }

    const lines: string[] = text.split('\n');
    const result: string[] = [];
    let inTable: boolean = false;
    let listTag: string = '';

    for (const line of lines) {
      const trimmed: string = line.trim();

      // Detect markdown table rows: | col | col |
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        if (listTag) {
          result.push(`</${listTag}>`);
          listTag = '';
        }
        const cells: string[] = trimmed
          .slice(1, -1)
          .split('|')
          .map((c: string): string => c.trim());

        // Separator row: every cell is only dashes/colons/spaces e.g. |---|:--:|
        const isSeparator: boolean = cells.length > 0 && cells.every((c: string): boolean => /^[-:\s]+$/.test(c));
        if (isSeparator) {
          continue;
        }

        if (!inTable) {
          result.push('<table class="agent-table"><thead><tr>');
          cells.forEach((c: string): void => { result.push(`<th>${this.inlineMarkdown(c)}</th>`); });
          result.push('</tr></thead><tbody>');
          inTable = true;
          continue;
        }

        result.push('<tr>');
        cells.forEach((c: string): void => { result.push(`<td>${this.inlineMarkdown(c)}</td>`); });
        result.push('</tr>');
        continue;
      }

      // Close table if we were in one
      if (inTable) {
        result.push('</tbody></table>');
        inTable = false;
      }

      // Ordered list: 1. item
      const orderedMatch: RegExpMatchArray | null = trimmed.match(/^(\d+)\.\s+(.+)$/);
      if (orderedMatch) {
        if (listTag !== 'ol') {
          if (listTag) { result.push(`</${listTag}>`); }
          result.push('<ol class="agent-list">');
          listTag = 'ol';
        }
        result.push(`<li>${this.inlineMarkdown(orderedMatch[2])}</li>`);
        continue;
      }

      // Unordered list: - item or * item
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (listTag !== 'ul') {
          if (listTag) { result.push(`</${listTag}>`); }
          result.push('<ul class="agent-list">');
          listTag = 'ul';
        }
        result.push(`<li>${this.inlineMarkdown(trimmed.slice(2))}</li>`);
        continue;
      }

      // Close list if we were in one
      if (listTag) {
        result.push(`</${listTag}>`);
        listTag = '';
      }

      // Regular line — apply inline markdown
      if (trimmed.length === 0) {
        result.push('<br>');
      } else {
        result.push(`<p>${this.inlineMarkdown(trimmed)}</p>`);
      }
    }

    if (inTable) {
      result.push('</tbody></table>');
    }
    if (listTag) {
      result.push(`</${listTag}>`);
    }

    return result.join('');
  }

  /** Convert inline markdown: **bold**, `code` */
  private inlineMarkdown(text: string): string {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`(.+?)`/g, '<code>$1</code>');
  }

  /**
   * Add system message to chat
   */
  private addSystemMessage(content: string): void {
    const messageContent: string = this.extractStringFromMessage(content);
    this.messages.push({
      content: messageContent,
      role: 'system',
      timestamp: new Date()
    });
  }

  /**
   * Find the last assistant message in the conversation.
   */
  private findLastAssistantMessage(): ConversationMessage | undefined {
    for (let i: number = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].role === 'assistant') {
        return this.messages[i];
      }
    }
    return undefined;
  }

  /**
   * Strip fenced code blocks from the assistant message.
   * Removes ```lang ... ``` blocks so the chat only shows conversational text.
   */
  private stripCodeBlocks(message: string): string {
    if (!message) {
      return '';
    }

    // Remove fenced code blocks: ```lang\n...\n``` (handles \r\n and optional lang tag)
    let cleaned: string = message.replace(/```[\w]*\r?\n[\s\S]*?```/g, '');

    // Also remove inline-style code blocks without newline after lang (```css ...```)
    cleaned = cleaned.replace(/```[\w]+[\s\S]*?```/g, '');

    // Clean up excessive blank lines left behind
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();

    return cleaned || message; // Fallback to original if everything was code
  }

  /**
   * Build full HTML document for live preview iframe.
   * Builds from local @Input values + pending changes (sync).
   */
  public buildPreviewHtml(): void {
    if (!this.pendingChanges) {
      this.previewHtml = '';
      this.previewHtmlApplied = '';
      return;
    }

    this.previewHtml = this.buildLocalPreviewHtml();
  }

  /**
   * Build preview HTML locally from current @Input values + pending changes.
   * Renders controls using the same structure as the backend.
   */
  private buildLocalPreviewHtml(): string {
    const changes: PageGenerationResult = this.pendingChanges as PageGenerationResult;
    const css: string = changes.css !== undefined ? changes.css : (this.currentCss || '');
    const headerHtml: string = changes.headerHtml !== undefined ? changes.headerHtml : (this.currentHtml || '');
    const footerHtml: string = changes.footerHtml !== undefined ? changes.footerHtml : (this.currentFooterHtml || '');
    const js: string = changes.javascript !== undefined ? changes.javascript : (this.currentScript || '');

    // Render controls matching backend structure (form-group divs, inputs, labels)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const controls: any[] = changes.controls || this.currentControls || [];
    let controlsHtml: string = '';
    if (controls.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      controlsHtml = controls.map((c: any): string => this.generateControlHtml(c)).join('\n');
    }

    /* eslint-disable no-useless-escape */
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>${css}</style>
</head>
<body>
  ${headerHtml}
  <div id="page-content">
    <form>
      ${controlsHtml}
      <div class="button-group">
        <input type="submit" value="Submit" />
      </div>
    </form>
  </div>
  ${footerHtml}
  <script>${js}<\/script>
</body>
</html>`;
    /* eslint-enable no-useless-escape */
  }

  /**
   * Generate HTML for a single control matching backend drawElement/drawCommonControls structure.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private generateControlHtml(control: any): string {
    if (!control) {
      return '';
    }
    const id: string = control.internalID || control.id || '';
    const cssClass: string = control.cssClass || '';
    const content: string = control.content || control.label || control.name || '';
    const description: string = control.description || '';

    // If control already has pre-rendered HTML, use it directly
    if (control.html) {
      return control.html;
    }

    switch (control.type) {
      case 'core-preference':
      case 'core-contact': {
        const prefix: string = control.type === 'core-preference' ? 'CP_' : 'CC_';
        const name: string = prefix + (id || '').replace(/\s/g, '_');
        const controlType: string = (control.controlType || control.fieldType || 'chk').toLowerCase();
        return this.drawFormElement(controlType, name, content, description);
      }
      case 'label':
        return `<div class="form-group" id="C-${cssClass}_div"><label class="form-label">${content}</label></div>`;
      case 'header':
        return `<div class="form-group" id="C-${cssClass}_div"><h1 class="form-header">${content}</h1></div>`;
      case 'subheader':
        return `<div class="form-group" id="C-${cssClass}_div"><h3 class="form-subheader">${content}</h3></div>`;
      case 'text-section':
        return `<div class="form-group"><p>${content}</p></div>`;
      case 'divider':
        return `<div class="form-group" id="C-${cssClass}_div"><hr class="form-divider" /></div>`;
      case 'page-section': {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const childrenHtml: string = (control.children || []).map((child: any): string =>
          this.generateControlHtml(child)
        ).join('\n');
        return `<section class="${cssClass}">${childrenHtml}</section>`;
      }
      default:
        return `<div class="form-group">${content}</div>`;
    }
  }

  /**
   * Draw a form element matching backend drawElement structure.
   */
  private drawFormElement(type: string, name: string, label: string, description: string): string {
    const descHtml: string = description ? `<small class="form-text text-muted">${description}</small>` : '';

    switch (type) {
      case 'chk':
      case 'checkbox':
      case 'sch':
        return `<div class="form-group checkbox-div" id="${name}_div">
  <input type="hidden" name="${name}" value="" />
  <input type="checkbox" name="${name}" id="${name}" />
  <div><label>${label}</label>${descHtml}</div>
</div>`;
      case 'rad':
      case 'radio':
        return `<div class="form-group radio-div" id="${name}_div">
  <input type="radio" name="${name}" id="${name}" />
  <label>${label}</label>${descHtml}
</div>`;
      case 'txt':
      case 'ltx':
        return `<div class="form-group text-div" id="${name}_div">
  <label>${label}</label>
  <input type="text" name="${name}" id="${name}" />${descHtml}
</div>`;
      case 'drp':
        return `<div class="form-group text-div" id="${name}_div">
  <label>${label}</label>
  <select name="${name}" id="${name}"><option value="">Select...</option></select>${descHtml}
</div>`;
      default:
        return `<div class="form-group checkbox-div" id="${name}_div">
  <input type="checkbox" name="${name}" id="${name}" />
  <div><label>${label}</label>${descHtml}</div>
</div>`;
    }
  }

  /**
   * Close assistant
   */
  public close(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.modalClosed.emit();
  }

  /**
   * Clear conversation and start fresh
   */
  public async clearConversation(): Promise<void> {
    if (this.conversationId) {
      try {
        await this.anthropicService.deleteConversation(this.conversationId).toPromise();
      } catch {
        // Ignore deletion errors
      }
    }

    this.conversationId = undefined;
    this.messages = [];
    this.pendingChanges = undefined;
    this.showPreview = false;
    this.clearAttachedImages();

    await this.initializeConversation();
  }

  /**
   * Get thumbnail URL for image
   */
  public getImageThumbnail(image: AttachedImage): string {
    return `data:${image.mediaType};base64,${image.base64Data}`;
  }

  /**
   * Handle enter key in textarea
   */
  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Send a message to the AI agent (write infrastructure).
   * Carries conversationId for multi-turn context.
   */
  public sendAgentMessage(userMessage: string): void {
    if (!userMessage?.trim() || this.isProcessing) {
      return;
    }
    this.isProcessing = true;
    this.lastCreatedPageEntity = undefined;
    this.lastCreatedChildEntities = [];

    const request: AgentRequest = {
      conversationId: this.agentConversationId ?? undefined,
      userMessage: userMessage.trim()
    };

    this.aiAgentService.handleTurn(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      error: (): void => {
        this.addAssistantMessage('An error occurred. Please try again.');
        this.isProcessing = false;
      },
      next: (response: AgentResponse): void => {
        this.agentConversationId = response.conversationId;

        if (response.requiresConfirmation) {
          this.pendingAgentConfirmation = {
            conversationId: response.conversationId,
            summary: response.pendingConfirmationSummary ?? response.assistantMessage
          };
        }

        if (response.assistantMessage) {
          this.addAssistantMessage(response.assistantMessage);
        }

        if (response.createdEntities?.length) {
          const entityList: string = response.createdEntities
            .map((e: CreatedEntitySummary): string => `${e.entityType}: ${e.name}`)
            .join(', ');
          this.addSystemMessage(`Created: ${entityList}`);
          this.updateCreatedEntityCard(response.createdEntities);
        }

        if (!response.success && response.errorMessage) {
          this.addAssistantMessage(response.errorMessage);
        }

        this.isProcessing = false;
      }
    });
  }

  /**
   * Confirm the pending agent write action.
   */
  public confirmAgentAction(): void {
    if (!this.pendingAgentConfirmation) {
      return;
    }
    this.isProcessing = true;
    this.lastCreatedPageEntity = undefined;
    this.lastCreatedChildEntities = [];

    this.aiAgentService.confirmAction(
      this.pendingAgentConfirmation.conversationId
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      error: (): void => {
        this.pendingAgentConfirmation = undefined;
        this.isProcessing = false;
      },
      next: (response: AgentResponse): void => {
        this.pendingAgentConfirmation = undefined;
        if (response.assistantMessage) {
          this.addAssistantMessage(response.assistantMessage);
        }
        if (response.createdEntities?.length) {
          const entityList: string = response.createdEntities
            .map((e: CreatedEntitySummary): string => `${e.entityType}: ${e.name}`)
            .join(', ');
          this.addSystemMessage(`Created: ${entityList}`);
          this.updateCreatedEntityCard(response.createdEntities);
        }
        this.isProcessing = false;
      }
    });
  }

  /**
   * Reject the pending agent write action.
   */
  public rejectAgentAction(): void {
    if (!this.pendingAgentConfirmation) {
      return;
    }
    this.isProcessing = true;

    this.aiAgentService.rejectAction(
      this.pendingAgentConfirmation.conversationId
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      error: (): void => {
        this.pendingAgentConfirmation = undefined;
        this.isProcessing = false;
      },
      next: (response: AgentResponse): void => {
        this.pendingAgentConfirmation = undefined;
        if (response.assistantMessage) {
          this.addAssistantMessage(response.assistantMessage);
        } else {
          this.addSystemMessage('Action cancelled.');
        }
        this.isProcessing = false;
      }
    });
  }

  /**
   * TrackBy function for the child entities list in the result card.
   */
  public trackByEntityId(_index: number, entity: CreatedEntitySummary): string {
    return entity.entityId;
  }

  /**
   * Navigate to the entity's detail page based on its type.
   */
  public navigateToEntity(entity: CreatedEntitySummary): void {
    if (entity.entityType === 'PreferencePage') {
      this.router.navigate(['/preferences/pages', entity.entityId]);
    } else if (entity.entityType === 'CorePreference') {
      this.router.navigate(['/preferences/core', entity.entityId]);
    }
  }

  /**
   * Extract the PreferencePage entity and its siblings from a created entities list.
   * Sets lastCreatedPageEntity and lastCreatedChildEntities for template rendering.
   */
  private updateCreatedEntityCard(entities: CreatedEntitySummary[]): void {
    const pageEntity: CreatedEntitySummary | undefined = entities.find(
      (e: CreatedEntitySummary): boolean => e.entityType === 'PreferencePage'
    );
    if (pageEntity) {
      this.lastCreatedPageEntity = pageEntity;
      this.lastCreatedChildEntities = entities.filter(
        (e: CreatedEntitySummary): boolean => e.entityType !== 'PreferencePage'
      );
    }
  }
}
